import {
  Form as FormikForm,
  Formik,
  type FormikConfig,
  type FormikProps,
} from 'formik';
import { isFunction, noop } from 'lodash';
import { type FormEventHandler, type ReactElement, useMemo } from 'react';
import { Box } from '@mui/material';

type TField =
  | string
  | number
  | boolean
  | Date
  | ReactElement
  | undefined
  | object;

export type FormValues<TForm> = Partial<
  Record<
    keyof TForm,
    TField | TField[] | Record<string, TField> | Record<string, TField>[]
  >
>;

export interface FormProps<TForm extends FormValues<TForm>> {
  initialValues: TForm;
  children?:
    | ReactElement
    | ReactElement[]
    | ((props: FormikProps<TForm>) => ReactElement)
    | ((props: FormikProps<TForm>) => ReactElement[]);
  onChange?: FormEventHandler<HTMLFormElement>;
  onSubmit?: FormikConfig<TForm>['onSubmit'];
  validate?: FormikConfig<TForm>['validate'];
  enableReinitialize?: boolean;
  innerRef?: FormikConfig<TForm>['innerRef'];
  className?: string;
}

export function Form<TForm extends FormValues<TForm>>({
  initialValues,
  children,
  onChange = noop,
  onSubmit = noop,
  validate = noop,
  enableReinitialize = false,
  innerRef,
  className = '',
}: FormProps<TForm>) {
  const formikProps: FormikConfig<TForm> = useMemo(
    () => ({ initialValues, onSubmit, validate, enableReinitialize, innerRef }),
    [initialValues, onSubmit, validate, enableReinitialize],
  );

  return (
    <Formik<TForm> {...formikProps}>
      {(props) => (
        <FormikForm
          className={`d-flex flex-column flex-grow-1 ${className}`}
          onChange={onChange}
          autoComplete="off"
        >
          {isFunction(children) ? children(props) : children}
        </FormikForm>
      )}
    </Formik>
  );
}
